import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, UserPlus, Users as UsersIcon, CreditCard, Pencil, UserX, UserCheck, X } from 'lucide-react';
import type { MemberSummary, MembershipStatus, PackageKind } from '@techsofit/api-types';
import { Button, Card, StatusBadge, Avatar, EmptyState, Skeleton, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { ActionMenu, type ActionItem } from '../components/ActionMenu';
import { UpgradeBanner, UpgradeModal, NEAR_LIMIT_RATIO } from '../components/Upgrade';
import { SubscriptionBanner } from '../components/SubscriptionBanner';
import { listMembers, deactivateMember, activateMember } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { useGymData } from '../data/GymDataProvider';
import { statusToBadge } from '../lib/membership';
import { formatDate, relativeDays } from '../lib/format';

type PackageFilter = 'ALL' | 'HAS' | 'ACTIVE' | 'EXPIRING' | 'EXPIRED' | 'NEVER';
type KindFilter = '' | PackageKind;

type Filter = 'ALL' | 'NEW' | MembershipStatus;

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'NEW', label: 'New this month' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'EXPIRING', label: 'Expiring' },
  { key: 'LAPSED', label: 'Lapsed' },
  { key: 'NEVER_PAID', label: 'Never paid' },
];

const PACKAGE_FILTERS: { key: PackageFilter; label: string }[] = [
  { key: 'ALL', label: 'All members' },
  { key: 'HAS', label: 'Has package' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'EXPIRING', label: 'Expiring' },
  { key: 'EXPIRED', label: 'Expired' },
  { key: 'NEVER', label: 'Never bought' },
];

function matchesCoach(m: MemberSummary, coachId: string) {
  if (!coachId) return true;
  if (m.coachId === coachId) return true;
  return (m.packageCoachIds ?? []).includes(coachId);
}

function matchesKind(m: MemberSummary, kind: KindFilter) {
  if (!kind) return true;
  const kinds = m.packageKinds ?? [];
  if (kinds.length) return kinds.includes(kind);
  return kind === 'PERSONAL_TRAINING' && m.ptStatus !== 'NEVER_PAID';
}

function matchesSold(m: MemberSummary, sold: string) {
  if (!sold) return true;
  if (sold === 'PERSONAL_TRAINING' || sold === 'CLASS') return matchesKind(m, sold as KindFilter);
  return (m.packagePlanIds ?? []).includes(sold);
}

function matchesPackage(m: MemberSummary, pkg: PackageFilter) {
  if (pkg === 'ALL') return true;
  if (pkg === 'HAS') return m.ptStatus !== 'NEVER_PAID';
  if (pkg === 'ACTIVE') return m.ptStatus === 'ACTIVE';
  if (pkg === 'EXPIRING') return m.ptStatus === 'EXPIRING';
  if (pkg === 'EXPIRED') return m.ptStatus === 'LAPSED';
  return m.ptStatus === 'NEVER_PAID';
}

/** First day of the current month, as an ISO date — the cutoff for "new this month". */
function monthStartIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export function MembersPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { subscription, coaches, plans } = useGymData();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  // Deep-link support: the dashboard "New members" card and Reports membership cards link here
  // with ?filter=<status> (new / active / expiring / lapsed / never_paid); Reports "Members by
  // plan" rows link with ?plan=<name>.
  const [filter, setFilter] = useState<Filter>(() => {
    const f = (searchParams.get('filter') ?? '').toUpperCase();
    return (FILTERS as { key: Filter }[]).some((x) => x.key === f) ? (f as Filter) : 'ALL';
  });
  const [coachFilter, setCoachFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState<PackageFilter>('ALL');
  const [soldFilter, setSoldFilter] = useState('');
  const [planFilter, setPlanFilter] = useState(searchParams.get('plan') ?? '');
  // The Corporate page links here with ?corporate=<id>&company=<name>.
  const [corporateFilter, setCorporateFilter] = useState(searchParams.get('corporate') ?? '');
  const corporateName = searchParams.get('company') ?? '';
  // The "Former members" nav item deep-links here with ?view=former (the deactivated roster).
  const [showInactive, setShowInactive] = useState(searchParams.get('view') === 'former');
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Fetch the tenant-scoped roster (active, or deactivated for the Inactive view);
  // filter/count client-side. Refetches when the active/inactive view changes.
  const { data, loading, error, reload } = useAsync(
    () => listMembers({ size: 1000, active: !showInactive }),
    [showInactive],
  );
  const members = useMemo(() => data?.items ?? [], [data]);

  // Paid-plan member cap: block "Add member" at the cap (the API enforces it too) and nudge
  // once ~80% full. Only a paid plan has a real cap — trial/grace are unlimited, and a lapsed
  // subscription is handled by the renew banner + the lock screen, not a fake "X/X" fraction.
  // Cap math is the active roster only — skip it in the Inactive view.
  const paidLimit = subscription.status === 'ACTIVE' ? subscription.effectiveMemberLimit : null;
  const atLimit = !showInactive && paidLimit != null && members.length >= paidLimit;
  const nearLimit = paidLimit != null && members.length >= Math.ceil(paidLimit * NEAR_LIMIT_RATIO);
  const showUpgrade = !showInactive && nearLimit;
  const goAddMember = () => (atLimit ? setUpgradeOpen(true) : navigate('/members/new'));
  const extraPlans = useMemo(
    () => plans.filter((p) => p.type === 'PERSONAL_TRAINING' || p.packageKind),
    [plans],
  );
  const soldLabel =
    soldFilter === 'PERSONAL_TRAINING' ? 'Any personal training'
      : soldFilter === 'CLASS' ? 'Any class'
        : extraPlans.find((p) => p.id === soldFilter)?.name ?? 'Selected';

  const counts = useMemo(() => {
    const monthStart = monthStartIso();
    const scoped = members.filter((m) => {
      if (planFilter) {
        const plan = m.currentPlanName ?? 'No plan';
        if (plan !== planFilter) return false;
      }
      if (corporateFilter && m.corporateClientId !== corporateFilter) return false;
      if (!matchesCoach(m, coachFilter)) return false;
      if (!matchesSold(m, soldFilter)) return false;
      if (!matchesPackage(m, packageFilter)) return false;
      return true;
    });
    const c: Record<Filter, number> = { ALL: scoped.length, NEW: 0, ACTIVE: 0, EXPIRING: 0, LAPSED: 0, NEVER_PAID: 0 };
    for (const m of scoped) {
      c[m.status]++;
      if (m.joinDate >= monthStart) c.NEW++;
    }
    return c;
  }, [members, planFilter, corporateFilter, coachFilter, soldFilter, packageFilter]);

  const packageCounts = useMemo(() => {
    const scoped = members.filter((m) => {
      if (filter === 'NEW') {
        if (m.joinDate < monthStartIso()) return false;
      } else if (filter !== 'ALL' && m.status !== filter) return false;
      if (planFilter) {
        const plan = m.currentPlanName ?? 'No plan';
        if (plan !== planFilter) return false;
      }
      if (corporateFilter && m.corporateClientId !== corporateFilter) return false;
      if (!matchesCoach(m, coachFilter)) return false;
      if (!matchesSold(m, soldFilter)) return false;
      return true;
    });
    const c: Record<PackageFilter, number> = { ALL: scoped.length, HAS: 0, ACTIVE: 0, EXPIRING: 0, EXPIRED: 0, NEVER: 0 };
    for (const m of scoped) {
      if (m.ptStatus !== 'NEVER_PAID') c.HAS++;
      if (m.ptStatus === 'ACTIVE') c.ACTIVE++;
      if (m.ptStatus === 'EXPIRING') c.EXPIRING++;
      if (m.ptStatus === 'LAPSED') c.EXPIRED++;
      if (m.ptStatus === 'NEVER_PAID') c.NEVER++;
    }
    return c;
  }, [members, filter, planFilter, corporateFilter, coachFilter, soldFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const monthStart = monthStartIso();
    return members.filter((m) => {
      if (filter === 'NEW') {
        if (m.joinDate < monthStart) return false;
      } else if (filter !== 'ALL' && m.status !== filter) return false;
      if (planFilter) {
        const plan = m.currentPlanName ?? 'No plan';
        if (plan !== planFilter) return false;
      }
      if (corporateFilter && m.corporateClientId !== corporateFilter) return false;
      if (!matchesCoach(m, coachFilter)) return false;
      if (!matchesSold(m, soldFilter)) return false;
      if (!matchesPackage(m, packageFilter)) return false;
      if (!q) return true;
      return m.fullName.toLowerCase().includes(q) || m.phone.includes(q) || (m.whatsappPhone ?? '').includes(q);
    });
  }, [query, filter, coachFilter, packageFilter, soldFilter, planFilter, corporateFilter, members]);

  async function onDeactivate(m: MemberSummary) {
    const ok = await confirm({
      title: `Deactivate ${m.fullName}?`,
      description: "They'll be hidden from the members list.",
      confirmLabel: 'Deactivate',
      tone: 'danger',
    });
    if (!ok) return;
    await deactivateMember(m.id);
    reload();
  }

  async function onReactivate(m: MemberSummary) {
    const ok = await confirm({
      title: `Reactivate ${m.fullName}?`,
      description: "They'll return to the members list.",
      confirmLabel: 'Reactivate',
    });
    if (!ok) return;
    await activateMember(m.id);
    reload();
  }

  function actionsFor(m: MemberSummary): ActionItem[] {
    // Deactivated members only offer reactivation.
    if (!m.active) {
      return [
        {
          label: 'Reactivate',
          icon: <UserCheck className="size-4" aria-hidden />,
          onClick: () => onReactivate(m),
        },
      ];
    }
    // Reminders (membership + PT) are fully automatic now — no manual "send" actions here.
    const items: ActionItem[] = [
      {
        label: 'Record payment',
        icon: <CreditCard className="size-4" aria-hidden />,
        onClick: () => navigate('/payments/new', { state: { memberId: m.id } }),
      },
      {
        label: 'Edit member',
        icon: <Pencil className="size-4" aria-hidden />,
        onClick: () => navigate(`/members/${m.id}/edit`),
      },
      {
        label: 'Deactivate',
        icon: <UserX className="size-4" aria-hidden />,
        danger: true,
        onClick: () => onDeactivate(m),
      },
    ];
    return items;
  }

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={`${members.length} ${showInactive ? 'inactive' : 'members'}`}
        title="Members"
        description="Open a member to see their record, or use the menu to act without leaving the list."
        actions={
          <Button variant="dark" onClick={goAddMember}>
            <UserPlus className="size-4" aria-hidden />
            <span className="hidden sm:inline">Add member</span>
          </Button>
        }
      />

      {/* Subscription state (lapsed / in grace / expiring) → renew nudge. */}
      <SubscriptionBanner className="mb-4" />

      {/* Genuine paid-plan cap → upgrade nudge with the real X/Y fraction. */}
      {showUpgrade && (
        <UpgradeBanner
          used={members.length}
          limit={paidLimit!}
          trial={false}
          onUpgrade={() => setUpgradeOpen(true)}
          className="mb-4"
        />
      )}

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} trial={false} limit={paidLimit} />

      {/* Active vs deactivated roster */}
      <div className="mb-4 flex gap-1 border-b border-line">
        <MemberTab label="Active" active={!showInactive} onClick={() => { setShowInactive(false); setFilter('ALL'); }} />
        <MemberTab label="Former" active={showInactive} onClick={() => { setShowInactive(true); setFilter('ALL'); }} />
      </div>

      {/* Controls */}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            type="search"
            inputMode="search"
            placeholder="Search by name or phone…"
            aria-label="Search members"
            className="h-touch w-full rounded-control border border-line bg-elevated pl-10 pr-4 text-ink placeholder:text-muted focus:border-ink-soft"
          />
        </div>

        <div className="flex items-center gap-2">
          {coaches.length > 0 && (
            <select
              value={coachFilter}
              onChange={(e) => setCoachFilter(e.target.value)}
              aria-label="Filter by coach"
              className="h-touch shrink-0 rounded-control border border-line bg-elevated px-3 text-sm text-ink focus:border-ink-soft"
            >
              <option value="">All coaches</option>
              {coaches.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.fullName}
                </option>
              ))}
            </select>
          )}

          <select
            value={soldFilter}
            onChange={(e) => setSoldFilter(e.target.value)}
            aria-label="Filter by package"
            className="h-touch shrink-0 rounded-control border border-line bg-elevated px-3 text-sm text-ink focus:border-ink-soft"
          >
            <option value="">All packages</option>
            <option value="PERSONAL_TRAINING">Any personal training</option>
            <option value="CLASS">Any class</option>
            {extraPlans.length > 0 && extraPlans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {PACKAGE_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setPackageFilter(f.key)}
            aria-pressed={packageFilter === f.key}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-pill px-3 py-1.5 text-sm font-medium transition-colors',
              packageFilter === f.key ? 'bg-ink text-on-dark' : 'bg-card text-ink-soft hover:bg-surface',
            )}
          >
            {f.label}
            <span className={cn('tabular text-xs', packageFilter === f.key ? 'text-on-dark-muted' : 'text-muted')}>
              {packageCounts[f.key]}
            </span>
          </button>
        ))}
      </div>

      <div className="mb-4 flex gap-1.5 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            aria-pressed={filter === f.key}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-pill px-3.5 py-2 text-sm font-medium transition-colors',
              filter === f.key ? 'bg-dark text-on-dark' : 'bg-card text-ink-soft hover:bg-surface',
            )}
          >
            {f.label}
            <span className={cn('tabular text-xs', filter === f.key ? 'text-on-dark-muted' : 'text-muted')}>
              {counts[f.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Active corporate filter (from the Corporate "View members" drill-in) */}
      {corporateFilter && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-2 rounded-pill bg-accent-soft px-3 py-1.5 font-medium text-ink">
            Company: {corporateName || 'Selected'}
            <button
              type="button"
              onClick={() => setCorporateFilter('')}
              aria-label="Clear company filter"
              className="grid size-4 place-items-center rounded-full text-muted hover:bg-ink/10 hover:text-ink"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </span>
          <span className="text-muted">
            {filtered.length} member{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {(coachFilter || packageFilter !== 'ALL' || soldFilter) && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          {coachFilter && (
            <span className="inline-flex items-center gap-2 rounded-pill bg-accent-soft px-3 py-1.5 font-medium text-ink">
              Coach: {coaches.find((c) => c.id === coachFilter)?.fullName ?? 'Selected'}
              <button
                type="button"
                onClick={() => setCoachFilter('')}
                aria-label="Clear coach filter"
                className="grid size-4 place-items-center rounded-full text-muted hover:bg-ink/10 hover:text-ink"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </span>
          )}
          {packageFilter !== 'ALL' && (
            <span className="inline-flex items-center gap-2 rounded-pill bg-accent-soft px-3 py-1.5 font-medium text-ink">
              Package: {PACKAGE_FILTERS.find((f) => f.key === packageFilter)?.label}
              <button
                type="button"
                onClick={() => setPackageFilter('ALL')}
                aria-label="Clear package filter"
                className="grid size-4 place-items-center rounded-full text-muted hover:bg-ink/10 hover:text-ink"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </span>
          )}
          {soldFilter && (
            <span className="inline-flex items-center gap-2 rounded-pill bg-accent-soft px-3 py-1.5 font-medium text-ink">
              Package: {soldLabel}
              <button
                type="button"
                onClick={() => setSoldFilter('')}
                aria-label="Clear package filter"
                className="grid size-4 place-items-center rounded-full text-muted hover:bg-ink/10 hover:text-ink"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </span>
          )}
          <span className="text-muted">
            {filtered.length} member{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {planFilter && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-2 rounded-pill bg-accent-soft px-3 py-1.5 font-medium text-ink">
            Plan: {planFilter}
            <button
              type="button"
              onClick={() => setPlanFilter('')}
              aria-label="Clear plan filter"
              className="grid size-4 place-items-center rounded-full text-muted hover:bg-ink/10 hover:text-ink"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </span>
          <span className="text-muted">
            {filtered.length} member{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {/* List */}
      <Card className="overflow-hidden p-0">
        {/* Desktop header row */}
        <div className="hidden grid-cols-[1.5fr_1fr_1fr_auto] gap-4 border-b border-line px-5 py-3 text-eyebrow uppercase text-muted md:grid">
          <span>Member</span>
          <span>Plan</span>
          <span>Status</span>
          <span className="text-right">Expiry</span>
        </div>

        {loading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon={<UsersIcon className="size-6" aria-hidden />}
            title="Couldn’t load members"
            description={error}
            className="rounded-none shadow-none"
          />
        ) : filtered.length ? (
          <ul className="divide-y divide-line">
            {filtered.map((m) => (
              <li key={m.id}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/members/${m.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/members/${m.id}`)}
                  className="grid w-full cursor-pointer grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface md:grid-cols-[1.5fr_1fr_1fr_auto] md:px-5"
                >
                  {/* Member */}
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar name={m.fullName} />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-ink">{m.fullName}</p>
                      <div className="flex items-center gap-1.5">
                        <p className="truncate text-sm text-muted tabular">{m.phone}</p>
                        {(m.ptStatus === 'EXPIRING' || m.ptStatus === 'LAPSED') && (
                          <span className={cn('shrink-0 rounded-pill px-1.5 py-0.5 text-[0.65rem] font-medium',
                            m.ptStatus === 'LAPSED' ? 'bg-bad-soft text-bad-ink' : 'bg-warn-soft text-warn-ink')}>
                            PT {m.ptStatus === 'LAPSED' ? 'expired' : 'expiring'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Plan (desktop) */}
                  <span className="hidden text-sm text-ink-soft md:block">{m.currentPlanName ?? '—'}</span>

                  {/* Status */}
                  <span className="hidden md:block">
                    <StatusBadge status={statusToBadge(m.status)} />
                  </span>

                  {/* Expiry + actions */}
                  <div className="flex items-center justify-end gap-1">
                    <div className="text-right">
                      <p className="text-sm font-medium text-ink tabular">{formatDate(m.currentExpiry)}</p>
                      <p className="text-xs text-muted tabular">{relativeDays(m.daysToExpiry)}</p>
                    </div>
                    <ActionMenu items={actionsFor(m)} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<UsersIcon className="size-6" aria-hidden />}
            title="No members match"
            description={
              coachFilter || packageFilter !== 'ALL' || soldFilter || filter !== 'ALL' || query
                ? 'Nothing matches these filters. Clear a coach, package, or status chip to widen the list.'
                : 'Try a different search or clear the status filter.'
            }
            className="rounded-none shadow-none"
          />
        )}
      </Card>
    </section>
  );
}

function MemberTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        '-mb-px shrink-0 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors',
        active ? 'border-ink text-ink' : 'border-transparent text-muted hover:text-ink',
      )}
    >
      {label}
    </button>
  );
}
